import { motion } from 'motion/react';
import { useMemo } from 'react';
import styles from './Confetti.module.scss';

export function Confetti() {
  // 1. Generate Static Rays
  const rays = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
    id: i,
    angle: (i * 360) / 12,
  })), []);

  // 2. Generate Grass Data
  const grassBlades = useMemo(() => 
    Array.from({ length: 45 }, (_, i) => ({
      id: i,
      left: (i * 100) / 45,
      height: 30 + Math.random() * 25, 
      windDelay: Math.random() * 2,
      lean: -10 + Math.random() * 20
    })), 
  []);

  // 3. Generate Sparkle Data
  const sparkles = useMemo(() => 
    Array.from({ length: 8 }, (_, i) => ({
      id: i,
      x: (Math.random() - 0.5) * 60,
      y: -Math.random() * 60,
      delay: 2.2 + i * 0.2
    })), 
  []);

  // 4. Generate Petals
  const petals = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
     id: i,
     angle: i * 30 
  })), []);

  return (
    <div className={styles.confetti}>
      {/* Sky background */}
      <motion.div
        className={styles.confetti__sky}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      />

      {/* Sun Container (Positioned to the Right) */}
      <motion.div
        className={styles.confetti__sunContainer}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          scale: { duration: 0.8, type: 'spring', bounce: 0.5 },
          opacity: { duration: 0.5 },
        }}
      >
        <svg width="150" height="150" viewBox="0 0 150 150">
          <defs>
            <radialGradient id="sunGradient" cx="50%" cy="50%">
              <stop offset="0%" style={{ stopColor: '#FFF59D', stopOpacity: 1 }} />
              <stop offset="70%" style={{ stopColor: '#FFD54F', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#FFA726', stopOpacity: 1 }} />
            </radialGradient>
          </defs>

          {/* ROTATING GROUP: Rays only */}
          <motion.g
            className={styles.confetti__sunRays}
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          >
              {/* Sun rays */}
              {rays.map((ray) => (
                <motion.g
                  key={ray.id}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: [0, 1.1, 1], opacity: 1 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.3 + ray.id * 0.03,
                    ease: 'easeOut',
                  }}
                >
                  <polygon
                    className={styles.confetti__sunRaysRay}
                    points="75,30 70,60 80,60"
                    transform={`rotate(${ray.angle} 75 75)`}
                  />
                </motion.g>
              ))}
          </motion.g>

          {/* STATIC GROUP: Sun circle (Does not rotate) */}
          <motion.circle
            className={styles.confetti__sunCenter}
            cx="75"
            cy="75"
            r="35"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, type: 'spring', bounce: 0.5 }}
          />

          {/* STATIC GROUP: Face (Does not rotate) */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.4 }}
          >
            {/* Eyes */}
            <circle cx="65" cy="70" r="3" fill="#FF6B35" />
            <circle cx="85" cy="70" r="3" fill="#FF6B35" />
            {/* Smile */}
            <path
              d="M 60 80 Q 75 90 90 80"
              stroke="#FF6B35"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
            />
          </motion.g>
        </svg>
      </motion.div>      

      {/* Grass Container */}
      <motion.div
        className={styles.confetti__grassContainer}
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        {grassBlades.map((blade) => (
          <motion.div
            key={blade.id}
            className={styles.confetti__grassBlade}
            initial={{ scaleY: 0, opacity: 0, rotate: blade.lean }}
            animate={{
                scaleY: 1,
                opacity: 1,
                rotate: [blade.lean, blade.lean + 15, blade.lean]
            }}
            transition={{
              scaleY: { duration: 0.4, delay: 0.7 + blade.id * 0.02 },
              rotate: {
                  duration: 2 + Math.random(),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: blade.windDelay
              }
            }}
            style={{
              '--blade-left': `${blade.left}%`,
              '--blade-height': `${blade.height}px`,
            } as React.CSSProperties}
          />
        ))}
      </motion.div>

      {/* Flower - Lowered Position */}
      <motion.div
        className={styles.confetti__flower}
        initial={{ y: 200, scale: 0, rotate: -180 }}
        animate={{ y: 0, scale: 1, rotate: 0 }}
        transition={{
          duration: 1,
          delay: 1.2,
          type: 'spring',
          bounce: 0.6,
        }}
      >
        <svg width="120" height="180" viewBox="0 0 120 180">
          <defs>
            <radialGradient id="petalGradient" cx="50%" cy="50%">
              <stop offset="0%" style={{ stopColor: '#FF6B9D', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#C2185B', stopOpacity: 1 }} />
            </radialGradient>
            <linearGradient id="stemGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#81C784', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#4CAF50', stopOpacity: 1 }} />
            </linearGradient>
          </defs>

          <motion.path
            className={styles.confetti__stem}
            d="M 60 80 Q 55 120 60 180"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
          />

          <motion.ellipse
            className={styles.confetti__leaf}
            cx="50"
            cy="110"
            rx="15"
            ry="8"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.4, delay: 1.5 }}
            transform="rotate(-30 50 110)"
          />
          <motion.ellipse
            className={styles.confetti__leaf}
            cx="70"
            cy="130"
            rx="15"
            ry="8"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.4, delay: 1.6 }}
            transform="rotate(30 70 130)"
          />

          <motion.g
            className={styles.confetti__flowerPetalsGroup}
            initial={{ scale: 0, rotate: 0 }}
            animate={{
              scale: 1,
              rotate: [0, -5, 5, -3, 3, 0],
            }}
            transition={{
              scale: { duration: 0.6, delay: 1.4, type: 'spring', bounce: 0.5 },
              rotate: {
                duration: 2,
                delay: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              },
            }}
          >
            {/* Petals */}
            {petals.map((petal) => (
              <g key={petal.id} transform={`rotate(${petal.angle} 60 50)`}>
                <motion.ellipse
                  className={styles.confetti__petal}
                  cx="60"
                  cy="30"
                  rx="12"
                  ry="20"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    duration: 0.4,
                    delay: 1.5 + petal.id * 0.05,
                    type: 'spring',
                    bounce: 0.6,
                  }}
                />
              </g>
            ))}

            <motion.circle
              className={styles.confetti__flowerCenter}
              cx="60"
              cy="50"
              r="12"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                duration: 0.5,
                delay: 1.8,
                type: 'spring',
                bounce: 0.7,
              }}
            />

            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <motion.circle
                key={i}
                cx={60 + Math.cos((i * Math.PI) / 4) * 5}
                cy={50 + Math.sin((i * Math.PI) / 4) * 5}
                r="1.5"
                fill="#F57C00"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  duration: 0.2,
                  delay: 1.9 + i * 0.03,
                }}
              />
            ))}
          </motion.g>
        </svg>
      </motion.div>

      {/* Sparkles */}
      {sparkles.map((sparkle) => (
        <motion.div
          key={sparkle.id}
          className={styles.confetti__sparkle}
          initial={{ scale: 0, opacity: 0 }}
          animate={{
            scale: [0, 1, 0],
            opacity: [0, 1, 0],
            x: [0, sparkle.x],
            y: [0, sparkle.y],
          }}
          transition={{
            duration: 1.5,
            delay: sparkle.delay,
            repeat: Infinity,
            repeatDelay: 1,
          }}
        >
          ✨
        </motion.div>
      ))}
    </div>
  );
}
